import { Button } from '@kcd/design-system';
import { trackClick, trackEvent } from '@kcd/event-tracker';

import { VENDORS_ROUTER } from '~/constants/router';
import { useRegisterMembership } from '~/domains/plusMembership/hooks';
import ProductPrice from '~/domains/products/components/ProductPrice';
import ProductImage from '~/domains/products/ProductImage';
import { useTrackProductEventProps } from '~/hooks/event';
import { useLinkComponent } from '~/hooks/mobileApp';
import useProductBottomSheetStore from '~/store/global/useProductBottomSheetStore';
import useProductOptionStore from '~/store/global/useProductOptionStore';
import type { OAISchema } from '~/types/openapi';

type Props = {
  data: OAISchema['MarketSelectProductResponseV2'];
};

const FrequentlyOrderedProductCard = ({ data }: Props) => {
  const LinkComponent = useLinkComponent();
  const { openBottomSheet } = useProductBottomSheetStore();
  const registerMembership = useRegisterMembership();
  const { setOfferId } = useProductOptionStore();
  const [{ onClick, trackAddCart }] = useTrackProductEventProps(data);

  const {
    offerId,
    vendorId,
    mainImages,
    productName,
    isOutOfStock,
    status,
    orderCount,
    showCashnotePlusSuggestion,
  } = data;

  const onClickProduct = () => {
    onClick?.();
  };

  const onClickActualOrder = () => {
    trackAddCart?.();

    trackEvent('clicked purchaseButton in 마켓', {
      placement: '상품상세',
      productName,
      offerId,
      path: window.location.pathname,
      showCashnotePlusSuggestion,
    });

    if (showCashnotePlusSuggestion) {
      trackClick({
        type: 'button',
        name: '마켓/멤버십혜택관',
        pageName: '마켓홈/멤버십혜택관',
      });

      void registerMembership('&salesTarget=CASHNOTE_PLUS');
      return;
    }

    openBottomSheet({ noticeType: 'tooltip', placement: '상품상세' });
    setOfferId(offerId!);
  };

  return (
    <div className="flex h-full flex-col">
      <div className="flex-1">
        <LinkComponent href={VENDORS_ROUTER.productDetail(vendorId, offerId!)}>
          <div role="presentation" onClick={onClickProduct}>
            <ProductImage
              width={140}
              height={140}
              sizes="45vw"
              layout="responsive"
              src={mainImages![0]}
              alt={productName}
              isOutOfStock={isOutOfStock}
              status={status}
              className="!mb-6"
            />
            {orderCount && (
              <div className="text-primary-regular typo-footnote300-semibold">
                {orderCount}회 이상 구매한 상품
              </div>
            )}
            <div
              className="mb-4 mt-6 line-clamp-1 whitespace-pre-line text-normal"
              style={{ fontSize: '14px' }}
            >
              {productName}
            </div>

            <div className="mb-6">
              <ProductPrice {...data} reverse />
            </div>
          </div>
        </LinkComponent>
      </div>
      <Button
        variant="outline"
        size="sm"
        block
        className="text-normal typo-paragraph300-semibold"
        onClick={onClickActualOrder}
      >
        재구매 하기
      </Button>
    </div>
  );
};

export default FrequentlyOrderedProductCard;
